// deps

// internal deps
import type { NativeRequestPayload, NativeRequest } from "@swiggy-private/native-bridge";
import { NativeResponseType, NativeRequestType, NativeBridge, init } from "@swiggy-private/native-bridge";
import { NB_PLATFORM } from "@swiggy-private/native-bridge";
import Blaze from "@swiggy-private/blaze.js";

import initWebSdk, { STATUS_CODES, ERROR_MESSAGES } from "@swiggy-private/web-bridge-sdk";

// helpers

import { blazeInit } from "hooks/useBootstrap";
import Router from "next/router";
import { isAndroid, isIOS, isGiftCardFlow } from "utils/utils";
import { PORTAL_SWIGGY_MONEY, SM_ROUTES } from "../constants";
import { createNBRequestInterceptor, createNBResponseInterceptor } from "./NativeBridgeInterceptor";

export const ANDROID_APP_USER_AGENT = "swiggy-android";
export const IOS_APP_USER_AGENT = "swiggy-ios";
export const APP_USER_AGENTS = [ANDROID_APP_USER_AGENT, IOS_APP_USER_AGENT];

export const getNativeBridgePlatform = (): NB_PLATFORM => {
    if (isAndroid()) {
        return NB_PLATFORM.ANDROID;
    }

    if (isIOS()) {
        return NB_PLATFORM.IOS;
    }

    return NB_PLATFORM.WEB;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const closeWebview = (payload: any) => {
    if (typeof window === "undefined") {
        return Promise.reject({
            statusMessage: "Window not available",
            statusCode: 1,
        });
    }
    if (payload?.params?.action === "ADD_VOUCHER_CLICK") {
        Router.replace(SM_ROUTES.VOUCHER_PATH);
    } else if (isGiftCardFlow()) {
        Router.back();
    } else {
        Router.replace(SM_ROUTES.LEDGER_PATH);
    }

    blazeInit(PORTAL_SWIGGY_MONEY);

    return Promise.resolve({} as NativeResponseType);
};

export const postOrderEvent = () => {
    if (typeof window === "undefined") {
        return Promise.reject({
            statusMessage: "Window not available",
            statusCode: 1,
        });
    }

    if (isGiftCardFlow()) {
        Router.replace(SM_ROUTES.GIFT_CARD_SHARING);
    } else {
        Router.replace(SM_ROUTES.LEDGER_PATH);
    }

    return Promise.resolve({} as NativeResponseType);
};

export default class WebBridgeSdk extends NativeBridge {
    private static instance: WebBridgeSdk | null = null;

    private constructor() {
        super(getNativeBridgePlatform());
    }

    public static getInstance = (): NativeBridge | undefined =>
        WebBridgeSdk.instance != null ? WebBridgeSdk.instance : undefined;

    private static initializeWebSdk() {
        // @ts-expect-error - web-bridge-sdk type definitions may need updating
        initWebSdk(WebBridgeSdk.requestHandler);
        init({ platform: getNativeBridgePlatform() });
        createNBRequestInterceptor(Blaze.recordCustomEvent, PORTAL_SWIGGY_MONEY);
        createNBResponseInterceptor(Blaze.recordCustomEvent, PORTAL_SWIGGY_MONEY);
    }

    public static setUpWebSdk() {
        if (WebBridgeSdk.instance != null || typeof window == "undefined") {
            return;
        }

        WebBridgeSdk.instance = new WebBridgeSdk();
        WebBridgeSdk.initializeWebSdk();
    }

    private static requestHandler = (
        req: NativeRequest<NativeRequestPayload>,
    ): Promise<{
        payload: NativeResponseType;
        statusCode?: number;
    }> => {
        return WebBridgeSdk.doHandleRequest(req).then((payload) => ({ payload }));
    };

    private static doHandleRequest = (req: NativeRequest<NativeRequestPayload>): Promise<NativeResponseType> => {
        switch (req.type) {
            case NativeRequestType.CLOSE_WEBVIEW:
                return closeWebview(req.payload);

            case NativeRequestType.POST_ORDER_EVENT:
                return postOrderEvent();

            default:
                return Promise.reject({
                    statusCode: STATUS_CODES.NOT_HANDLED,
                    statusMessage: ERROR_MESSAGES.PLUGIN_NOT_HANDLED,
                });
        }
    };
}
